import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Send,
  Sparkles,
  Crown,
  Wallet,
  CalendarClock,
  MessageCircle,
  Mail,
  BarChart3,
  ShieldCheck,
  Link2,
  Flag,
  CheckCircle2,
} from "lucide-react";

const TELEGRAM_HANDLE = "nusr11";
const TELEGRAM_URL = `https://t.me/${TELEGRAM_HANDLE}`;

const ColombiaFlag = ({ className = "w-7 h-5" }) => (
  <svg
    viewBox="0 0 6 4"
    className={`inline-block rounded-sm overflow-hidden shadow-md ${className}`}
    aria-label="Bandera de Colombia"
  >
    <rect width="6" height="2" fill="#FCD116" />
    <rect width="6" height="1" y="2" fill="#003893" />
    <rect width="6" height="1" y="3" fill="#CE1126" />
  </svg>
);

const FEATURES = [
  {
    icon: Wallet,
    title: "Hasta 95% de tus ganancias",
    desc: "Te quedas con casi todo lo que generas. Sin letras chiquitas.",
    accent: "from-amber-400 to-rose-400",
  },
  {
    icon: CalendarClock,
    title: "Pagos semanales en COP",
    desc: "Sin largas esperas. Cobra cada semana directo a tu cuenta colombiana.",
    accent: "from-emerald-400 to-cyan-400",
  },
  {
    icon: Flag,
    title: "Pionera en Colombia",
    desc: "Sé de las primeras embajadoras de una plataforma 100% colombiana hecha para ti.",
    accent: "from-yellow-400 to-orange-400",
  },
  {
    icon: ShieldCheck,
    title: "Sin coste de entrada",
    desc: "Cero inscripción, cero mensualidad. Solo crecer.",
    accent: "from-pink-400 to-purple-400",
  },
  {
    icon: Link2,
    title: "Tu link-in-bio premium",
    desc: "Perfil exclusivo con tus redes, contenido y suscripción. Diseño de lujo.",
    accent: "from-blue-400 to-indigo-400",
  },
  {
    icon: MessageCircle,
    title: "Chat privado, tips & PPV",
    desc: "Conversa con tus fans, recibe propinas y vende contenido bloqueado por pago.",
    accent: "from-rose-400 to-pink-400",
  },
  {
    icon: Mail,
    title: "Newsletter para tus fans",
    desc: "Mantén a tu audiencia enganchada con campañas de email a tus suscriptores.",
    accent: "from-teal-400 to-emerald-400",
  },
  {
    icon: BarChart3,
    title: "Dashboard en vivo",
    desc: "Estadísticas, ingresos y crecimiento en una sola pantalla.",
    accent: "from-sky-400 to-blue-400",
  },
  {
    icon: Crown,
    title: "Control total de tu contenido",
    desc: "Tú decides qué se ve, quién lo ve y cuándo. Privacidad real.",
    accent: "from-yellow-300 to-amber-500",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Escríbenos por Telegram",
    desc: `Toca el botón y mándanos un mensaje a @${TELEGRAM_HANDLE}.`,
  },
  {
    n: "02",
    title: "Te contamos los detalles",
    desc: "Conversamos sobre tu propuesta, tu nicho y tus metas. Cero compromiso.",
  },
  {
    n: "03",
    title: "Activamos tu perfil de Embajadora",
    desc: "Te abrimos tu cuenta, configuramos tus canales y empiezas a generar.",
  },
];

export default function EmbajadorasPage() {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // Autoplay blocked — wait for first user interaction
          const onInteract = () => {
            v.play().catch(() => {});
            window.removeEventListener("touchstart", onInteract);
            window.removeEventListener("click", onInteract);
          };
          window.addEventListener("touchstart", onInteract, { once: true });
          window.addEventListener("click", onInteract, { once: true });
        });
      }
    };
    tryPlay();
  }, []);

  return (
    <div className="relative min-h-screen text-white overflow-x-hidden" data-testid="embajadoras-page">
      <Helmet>
        <title>Embajadoras JGFans · Pionera en Colombia · Hasta 95% de tus ganancias</title>
        <meta
          name="description"
          content="Únete al equipo de Embajadoras JGFans, la plataforma pionera en Colombia para creadoras. Pagos semanales en COP, hasta 95% de tus ganancias, sin coste de entrada."
        />
        <meta property="og:title" content="Embajadoras JGFans · Pionera en Colombia" />
        <meta
          property="og:description"
          content="Pagos semanales · Hasta 95% de tus ganancias · Sin coste de entrada. Escríbenos por Telegram."
        />
      </Helmet>

      {/* Background video — fixed full-viewport, sits BEHIND content */}
      <div className="fixed inset-0 overflow-hidden bg-black" style={{ zIndex: 0 }}>
        <video
          ref={videoRef}
          src="/media/embajadoras-bg.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="embajadoras-bg-video"
        />
        {/* Darkening overlay — atenuación fuerte para que el contenido destaque */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/85" />
        <div className="absolute inset-0 bg-black/30" />
        {/* Ambient gradients on top of video */}
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-rose-500/10 blur-3xl" />
        <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full bg-amber-500/10 blur-3xl" />
        <div className="absolute -bottom-40 left-1/3 w-[600px] h-[600px] rounded-full bg-fuchsia-500/10 blur-3xl" />
      </div>

      {/* All content sits ABOVE the video */}
      <div className="relative" style={{ zIndex: 1 }}>

      {/* Nav */}
      <nav className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-6 flex items-center justify-between">
        <Link
          to="/"
          data-testid="back-home-link"
          className="text-white/60 hover:text-white text-sm flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
        <a
          href={TELEGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-telegram-link"
          className="text-xs sm:text-sm text-white/70 hover:text-white flex items-center gap-1.5"
        >
          <Send className="w-3.5 h-3.5" /> @{TELEGRAM_HANDLE}
        </a>
      </nav>

      {/* HERO */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-16">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-rose-500/20 to-amber-500/20 border border-rose-400/30 mb-6">
          <Sparkles className="w-3.5 h-3.5 text-rose-300" />
          <span className="text-[11px] uppercase tracking-[0.2em] text-rose-200 font-semibold">
            Próximamente · Cupos limitados
          </span>
        </div>

        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight max-w-4xl">
          Sé pionera.
          <br />
          <span className="bg-gradient-to-r from-rose-300 via-amber-200 to-rose-400 bg-clip-text text-transparent">
            Sé dueña de tu contenido.
          </span>
        </h1>

        <p className="mt-6 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed">
          Únete al equipo de <span className="text-white font-semibold">Embajadoras JGFans</span>,
          la primera plataforma colombiana pensada para creadoras que quieren monetizar de verdad.
          Sin ataduras, sin comisiones abusivas, sin esperar meses por tu plata.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-telegram-cta"
            className="group inline-flex items-center justify-center gap-2.5 px-7 h-14 rounded-full bg-gradient-to-r from-rose-500 to-amber-500 text-black font-bold text-base shadow-[0_10px_40px_-10px_rgba(244,114,182,0.6)] hover:shadow-[0_15px_50px_-10px_rgba(244,114,182,0.8)] hover:scale-[1.02] transition-all"
          >
            <Sparkles className="w-4 h-4" />
            Aplica aquí
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#beneficios"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center justify-center px-6 h-14 rounded-full border border-white/15 text-white/80 hover:text-white hover:bg-white/5 transition-all"
          >
            Ver beneficios
          </a>
        </div>

        {/* Pillars */}
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[
            { big: "95%", small: "Para ti", isFlag: false },
            { big: "Semanal", small: "Tus pagos", isFlag: false },
            { big: "$0", small: "Coste de entrada", isFlag: false },
            { big: "FLAG", small: "Made in Colombia", isFlag: true },
          ].map((p) => (
            <div
              key={p.small}
              className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-5 backdrop-blur-sm"
            >
              {p.isFlag ? (
                <ColombiaFlag className="w-12 h-8 sm:w-16 sm:h-10" />
              ) : (
                <div className="text-2xl sm:text-4xl font-black bg-gradient-to-r from-rose-300 to-amber-300 bg-clip-text text-transparent">
                  {p.big}
                </div>
              )}
              <div className="text-[11px] sm:text-xs uppercase tracking-wider text-white/50 mt-1">
                {p.small}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS GRID */}
      <section
        id="beneficios"
        className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24"
      >
        <div className="mb-12 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.25em] text-rose-300 mb-3 font-semibold">
            Lo que incluye ser Embajadora
          </p>
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight">
            Todo lo que necesitas para crecer,
            <br />
            <span className="text-white/50">sin todo lo que te frena.</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className="group relative bg-white/5 border border-white/10 rounded-2xl p-5 sm:p-6 hover:border-white/25 hover:bg-white/[0.07] transition-all"
              >
                <div
                  className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.accent} flex items-center justify-center mb-4 shadow-lg`}
                >
                  <Icon className="w-5 h-5 text-black" strokeWidth={2.5} />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">
                  {f.title}
                </h3>
                <p className="text-sm text-white/60 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* HOW TO APPLY */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="bg-gradient-to-br from-rose-500/10 via-fuchsia-500/5 to-amber-500/10 border border-white/10 rounded-3xl p-6 sm:p-12">
          <p className="text-xs uppercase tracking-[0.25em] text-amber-300 mb-3 font-semibold">
            Cómo aplicar
          </p>
          <h2 className="text-3xl sm:text-4xl font-black mb-10 max-w-2xl">
            3 pasos para empezar a ganar
          </h2>

          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                data-testid={`step-${i}`}
                className="bg-black/40 border border-white/10 rounded-2xl p-5 sm:p-6 relative"
              >
                <div className="text-5xl font-black text-white/10 absolute top-3 right-4 select-none">
                  {s.n}
                </div>
                <CheckCircle2 className="w-6 h-6 text-emerald-400 mb-3" />
                <h3 className="text-lg font-bold mb-1.5">{s.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 py-16 sm:py-24 text-center">
        <Crown className="w-10 h-10 text-amber-300 mx-auto mb-5" />
        <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-5">
          ¿Lista para ser una de
          <br />
          <span className="bg-gradient-to-r from-rose-300 via-amber-200 to-rose-400 bg-clip-text text-transparent">
            las primeras embajadoras?
          </span>
        </h2>
        <p className="text-white/70 text-base sm:text-lg max-w-xl mx-auto mb-8">
          Cupos limitados para esta primera generación. Escríbenos por Telegram y te
          contamos todo, sin compromiso.
        </p>

        <a
          href={TELEGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-telegram-cta"
          className="group inline-flex items-center justify-center gap-2.5 px-8 h-14 rounded-full bg-gradient-to-r from-rose-500 to-amber-500 text-black font-bold shadow-[0_10px_40px_-10px_rgba(244,114,182,0.6)] hover:shadow-[0_15px_50px_-10px_rgba(244,114,182,0.9)] hover:scale-[1.03] transition-all"
        >
          <Sparkles className="w-4 h-4" />
          Aplica aquí · Hablemos por Telegram
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </a>

        <p className="text-xs text-white/40 mt-8">
          JGFans · Plataforma colombiana para creadoras · Próximamente disponible para todas
        </p>
      </section>
      </div>
    </div>
  );
}
